import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { RefreshCw, Link2, Unlink, Star, AlertTriangle, CheckCircle2, Copy } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RESULT_MESSAGES = {
  connected: { ok: true, text: "Google connected. Your reviews are being imported now." },
  denied: { ok: false, text: "Google sign-in was cancelled." },
  invalid: { ok: false, text: "That sign-in link expired. Please try connecting again." },
  failed: { ok: false, text: "Google did not finish the sign-in. Please try again." },
};

const Notice = ({ ok, children, testId }) => (
  <div data-testid={testId} className={`flex items-start gap-2 rounded-lg px-4 py-3 text-sm ${ok ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-800"}`}>
    {ok ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" /> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />}
    <div>{children}</div>
  </div>
);

const SetupSteps = ({ redirectUri }) => (
  <ol className="list-decimal space-y-1 pl-5 text-sm text-slate-600" data-testid="google-setup-steps">
    <li>Request Business Profile API access for your Google Cloud project (Google reviews requests in about 14 days).</li>
    <li>Create an OAuth "Web application" client and add this redirect address:</li>
    <li className="list-none">
      <span className="flex items-center gap-2">
        <code className="break-all rounded bg-slate-100 px-2 py-1 text-xs" data-testid="google-redirect-uri">{redirectUri}</code>
        <button type="button" aria-label="Copy redirect address" onClick={() => navigator.clipboard?.writeText(redirectUri)} className="text-slate-500 hover:text-sky-600" data-testid="google-copy-redirect-btn">
          <Copy className="h-4 w-4" />
        </button>
      </span>
    </li>
    <li>Add the client ID and secret to the app, then press Connect Google.</li>
  </ol>
);

const ActionButton = ({ onClick, icon: Icon, children, primary, disabled, testId }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    data-testid={testId}
    className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50 ${primary ? "bg-sky-600 text-white hover:bg-sky-700" : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-100"}`}
  >
    <Icon className="h-4 w-4" /> {children}
  </button>
);

const AdminGoogleReviews = () => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const result = RESULT_MESSAGES[new URLSearchParams(window.location.search).get("google")];

  const load = useCallback(() => {
    axios.get(`${API}/admin/google/status`, { withCredentials: true }).then((r) => setStatus(r.data)).catch(() => setStatus(null));
  }, []);

  useEffect(() => { load(); }, [load]);

  const connect = async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/admin/google/connect`, { withCredentials: true });
      window.location.href = r.data.url;
    } catch {
      setBusy(false);
    }
  };
  const sync = async () => {
    setBusy(true);
    await axios.post(`${API}/admin/google/sync`, {}, { withCredentials: true }).catch(() => {});
    setTimeout(() => { load(); setBusy(false); }, 4000);
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnect Google? Imported reviews stay on the site.")) return;
    await axios.post(`${API}/admin/google/disconnect`, {}, { withCredentials: true }).catch(() => {});
    load();
  };

  if (!status) return <div className="py-16 text-center text-slate-500" data-testid="google-reviews-loading">Loading...</div>;

  return (
    <section className="space-y-5 rounded-xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="admin-google-reviews">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-50 text-sky-600"><Star className="h-5 w-5" /></div>
        <div>
          <h3 className="text-base font-bold text-slate-900">Google reviews</h3>
          <p className="text-sm text-slate-500">Every review on your Google Business Profile is imported and refreshed automatically each morning.</p>
        </div>
      </div>
      {result && <Notice ok={result.ok} testId="google-result-notice">{result.text}</Notice>}
      {!status.configured && <Notice testId="google-not-configured">Google sign-in keys have not been added yet.</Notice>}
      {status.last_error && <Notice testId="google-last-error">{status.last_error}</Notice>}
      <dl className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-4">
        <div><dt className="text-slate-500">Status</dt><dd className="font-semibold" data-testid="google-status">{status.connected ? "Connected" : "Not connected"}</dd></div>
        <div><dt className="text-slate-500">Business</dt><dd className="font-semibold">{status.location_title || "-"}</dd></div>
        <div><dt className="text-slate-500">Reviews imported</dt><dd className="font-semibold" data-testid="google-review-count">{status.review_count}</dd></div>
        <div><dt className="text-slate-500">Last update</dt><dd className="font-semibold">{status.last_sync ? new Date(status.last_sync).toLocaleString("en-GB") : "-"}</dd></div>
      </dl>
      <div className="flex flex-wrap gap-2">
        {!status.connected && <ActionButton primary icon={Link2} onClick={connect} disabled={!status.configured || busy} testId="google-connect-btn">Connect Google</ActionButton>}
        {status.connected && <ActionButton primary icon={RefreshCw} onClick={sync} disabled={busy} testId="google-sync-btn">{busy ? "Updating..." : "Update now"}</ActionButton>}
        {status.connected && <ActionButton icon={Unlink} onClick={disconnect} testId="google-disconnect-btn">Disconnect</ActionButton>}
      </div>
      {!status.connected && <SetupSteps redirectUri={status.redirect_uri} />}
    </section>
  );
};

export default AdminGoogleReviews;
